// src/vatandas/redux/slices/vatandasAuthSlice.js

import { createSlice } from "@reduxjs/toolkit";

const LS_KEYS = {
  user: "user",
  accessToken: "accessToken",
  refreshToken: "refreshToken", //   yeni (varsa saklarız)
};

/**
 * Güvenli localStorage erişimi (test/SSR ortamlarında patlamasın)
 */
const hasStorage = () => {
  try {
    return typeof window !== "undefined" && !!window.localStorage;
  } catch {
    return false;
  }
};

const readString = (key) => {
  if (!hasStorage()) return null;
  const v = localStorage.getItem(key);
  return v || null;
};

const writeString = (key, value) => {
  if (!hasStorage()) return;
  if (!value) localStorage.removeItem(key);
  else localStorage.setItem(key, value);
};

const readJSON = (key) => {
  if (!hasStorage()) return null;
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
};

const writeJSON = (key, value) => {
  if (!hasStorage()) return;
  if (value === null || value === undefined) localStorage.removeItem(key);
  else localStorage.setItem(key, JSON.stringify(value));
};

/**
 * Bazı eski akışlarda token JSON.stringify ile yazılmış olabiliyor:
 * "\"abc\"" -> JSON.parse -> "abc"
 */
const readMaybeJSONString = (key) => {
  const v = readString(key);
  if (!v) return null;

  try {
    const parsed = JSON.parse(v);
    if (typeof parsed === "string") return parsed;
  } catch {
    // ignore
  }
  return v;
};

const initialAccessToken = readMaybeJSONString(LS_KEYS.accessToken);
const initialRefreshToken = readMaybeJSONString(LS_KEYS.refreshToken);

const initialState = {
  user: readJSON(LS_KEYS.user),
  accessToken: initialAccessToken,
  refreshToken: initialRefreshToken,
  isAuthenticated: Boolean(initialAccessToken),
  error: null,
};

const vatandasAuthSlice = createSlice({
  name: "vatandasAuth",
  initialState,
  reducers: {
    /**
     *   Yeni: Tokenları ayrı yönetmek istersen
     * payload: { access, refresh }
     */
    setTokens(state, action) {
      const { access, refresh } = action.payload || {};
      state.accessToken = access || null;
      state.refreshToken = refresh || null;
      state.isAuthenticated = Boolean(access);
      state.error = null;

      writeString(LS_KEYS.accessToken, access || null);
      writeString(LS_KEYS.refreshToken, refresh || null);
    },

    /**
     *   Yeni: User set
     */
    setUser(state, action) {
      state.user = action.payload || null;
      state.error = null;
      writeJSON(LS_KEYS.user, state.user);
    },

    /**
     *   Backward compatible loginSuccess:
     * - Eski: { user, token }
     * - Yeni: { user, access, refresh }
     */
    loginSuccess(state, action) {
      const payload = action.payload || {};

      const user = payload.user ?? null;

      // legacy token veya access
      const access = payload.access || payload.token || null;
      const refresh = payload.refresh || payload.refreshToken || null;

      state.user = user;
      state.accessToken = access;
      state.refreshToken = refresh;
      state.isAuthenticated = Boolean(access);
      state.error = null;

      writeJSON(LS_KEYS.user, user);
      writeString(LS_KEYS.accessToken, access);
      writeString(LS_KEYS.refreshToken, refresh);
    },

    logout(state) {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;

      if (hasStorage()) {
        localStorage.removeItem(LS_KEYS.user);
        localStorage.removeItem(LS_KEYS.accessToken);
        localStorage.removeItem(LS_KEYS.refreshToken);
      }
    },

    loginFail(state, action) {
      state.error = action.payload || "Giriş başarısız.";
      // Not: mevcut session’ı bozmayalım diye tokenları burada silmiyoruz.
    },

    clearError(state) {
      state.error = null;
    },

    /**
     *   Uygulama açılışında storage -> state
     */
    hydrateFromStorage(state) {
      const access = readMaybeJSONString(LS_KEYS.accessToken);
      const refresh = readMaybeJSONString(LS_KEYS.refreshToken);
      const user = readJSON(LS_KEYS.user);

      state.accessToken = access;
      state.refreshToken = refresh;
      state.user = user;
      state.isAuthenticated = Boolean(access);
    },
  },
});

export const {
  setTokens,
  setUser,
  loginSuccess,
  logout,
  loginFail,
  clearError,
  hydrateFromStorage,
} = vatandasAuthSlice.actions;

export default vatandasAuthSlice.reducer;
